import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { ListService, CoreModule, PermissionService } from '@abp/ng.core';
import { PageModule } from '@abp/ng.components/page';
import {
  NgbDateAdapter,
  NgbCollapseModule,
  NgbDatepickerModule,
  NgbDropdownModule,
} from '@ng-bootstrap/ng-bootstrap';
import { Confirmation, ConfirmationService, DateAdapter, ThemeSharedModule } from '@abp/ng.theme.shared';
import { CommercialUiModule } from '@shared/commercial-ui';
import { AccountSettingsService } from '@proxy/account-settings';
import { filter, switchMap } from 'rxjs';
import { AbpOAuthService } from '@abp/ng.oauth';

@Component({
    selector: 'app-settings-tab',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './setting-tab.component.html',
    styleUrls: ['./setting-tab.component.scss'],
    imports: [
        NgbCollapseModule,
        NgbDatepickerModule,
        NgbDropdownModule,
        PageModule,
        CoreModule,
        ThemeSharedModule,
        CommercialUiModule,
    ],
    providers: [ListService, { provide: NgbDateAdapter, useClass: DateAdapter }]
})
export class SettingTabComponent {
  private readonly service = inject(AccountSettingsService);

  AIDetectionPermission = 'ReturnLy.Settings.EnableAIDetection';
  NotificationsPermission = 'ReturnLy.Settings.EnableNotifications';
  BothPermissions = 'ReturnLy.Settings.EnableAIDetection || ReturnLy.Settings.EnableNotifications'

  // Signals, not plain fields: set in subscribes, which an OnPush ancestor does not redraw for
  // (#42). They replace the markForCheck() workaround.
  readonly enableNotification = signal<boolean | undefined>(undefined);
  readonly enableAI = signal<boolean | undefined>(undefined);

  readonly isLoading = signal({
    enableNotification: true,
    enableAI: true,
  });

  constructor(
    private permissionService: PermissionService,
    private confirmationService: ConfirmationService,
    private oAuthService: AbpOAuthService
  ) {}

  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.
    const canGetAIDetectionEnabled = this.permissionService.getGrantedPolicy(
      this.AIDetectionPermission
    );
    const canGetNotificationEnabled = this.permissionService.getGrantedPolicy(
      this.NotificationsPermission
    );

    if (canGetAIDetectionEnabled) {
      this.getAIDetectionEnabled();
    }

    if (canGetNotificationEnabled) {
      this.getNotificationEnabled();
    }
  }

  changeNotification(e) {
    this.enableNotification.set(e?.target?.checked);
  }

  getNotificationEnabled() {
    this.service.getNotificationEnabled().subscribe({
      next: results => {
        this.setLoading('enableNotification', false);
        this.enableNotification.set(results);
      },
      error: () => {
        this.setLoading('enableNotification', false);
      },
    });
  }

  changeAIDetection(e) {
    this.enableAI.set(e?.target?.checked);
  }

  getAIDetectionEnabled() {
    this.service.getAIDetectionEnabled().subscribe({
      next: results => {
        this.setLoading('enableAI', false);
        this.enableAI.set(results);
      },
      error: () => {
        this.setLoading('enableAI', false);
      },
    });
  }

  saveNotification() {
    this.setLoading('enableNotification', true);
    this.service.setNotificationEnabled(this.enableNotification()).subscribe({
      next: this.getNotificationEnabled.bind(this),
      error: () => {
        this.setLoading('enableNotification', false);
      },
    });
  }

  saveAI() {
    this.setLoading('enableAI', true);
    this.service.setAIDetectionEnabled(this.enableAI()).subscribe({
      next: this.getAIDetectionEnabled.bind(this),
      error: () => {
        this.setLoading('enableAI', false);
      },
    });
  }

  private setLoading(key: 'enableNotification' | 'enableAI', loading: boolean) {
    this.isLoading.update(state => ({ ...state, [key]: loading }));
  }

  deleteMyAccount() {
    return this.confirmationService
      .warn('::AccountDeleteConfirmationMessage', '::AreYouSure', { messageLocalizationParams: [] })
      .pipe(
        filter(status => status === Confirmation.Status.confirm),
        switchMap(() => this.service.deleteMyAccount())
      )
      .subscribe({
        next:()=>{
          this.oAuthService.logout().subscribe();
          location.assign('/')
        }
      });
  }
}
